import "@/App.css";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { PlantProvider } from "@/context/PlantContext";
import Layout from "@/components/Layout";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import DetailedDashboard from "@/pages/DetailedDashboard";
import Materials from "@/pages/Materials";
import AnnexI from "@/pages/AnnexI";
import Uploads from "@/pages/Uploads";
import Scripts from "@/pages/Scripts";
import DataPage from "@/pages/DataPage";
import Plants from "@/pages/Plants";
import UsersPage from "@/pages/Users";
import Suppliers from "@/pages/Suppliers";
import SupplierDetail from "@/pages/SupplierDetail";
import Portal from "@/pages/portal/Portal";
import RiskDashboard from "@/pages/RiskDashboard";
import DdsRegister from "@/pages/dds/DdsRegister";
import DdsDetail from "@/pages/dds/DdsDetail";
import DdsDashboard from "@/pages/dds/DdsDashboard";
import Flows from "@/pages/dds/Flows";
import Mapping from "@/pages/dds/Mapping";
import TracesSettings from "@/pages/settings/TracesSettings";
import AuditLog from "@/pages/AuditLog";
import VendorPlants from "@/pages/VendorPlants";
import Packs from "@/pages/Packs";
import AccountManagers from "@/pages/AccountManagers";
import Overview from "@/pages/Overview";
import Readiness from "@/pages/Readiness";
import Inbox from "@/pages/Inbox";
import Trace from "@/pages/Trace";
import Explorer from "@/pages/Explorer";
import ProcessGuide from "@/pages/ProcessGuide";
import FirstTwoWeeksPlan from "@/pages/FirstTwoWeeksPlan";
import CodeLookup from "@/pages/CodeLookup";
import Customs from "@/pages/Customs";
import Untraceable from "@/pages/Untraceable";
import CountryRisk from "@/pages/settings/CountryRisk";
import RiskRules from "@/pages/settings/RiskRules";
import GfwSettings from "@/pages/settings/GfwSettings";
import DataHousekeeping from "@/pages/settings/DataHousekeeping";
import Notifications from "@/pages/settings/Notifications";
import CodeChanges from "@/pages/CodeChanges";

function Protected({ children, admin }) {
  const { user, can } = useAuth();
  if (user === null)
    return (
      <div className="flex h-screen items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
      </div>
    );
  if (!user) return <Navigate to="/login" replace />;
  if (admin && !can.admin) return <Navigate to="/" replace />;
  return children;
}

function Home() {
  const { can, user } = useAuth();
  if (can.accountManager) return <Navigate to="/overview" replace />;
  return user?.role === "viewer" ? <Dashboard /> : <Readiness />;
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <PlantProvider>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/portal/:token" element={<Portal />} />
            <Route
              element={
                <Protected>
                  <Layout />
                </Protected>
              }
            >
              <Route index element={<Home />} />
              <Route path="dashboard" element={<Dashboard />} />
              <Route path="dashboard/detailed" element={<DetailedDashboard />} />
              <Route path="inbox" element={<Inbox />} />
              <Route path="trace" element={<Trace />} />
              <Route path="explorer" element={<Explorer />} />
              <Route path="guide" element={<ProcessGuide />} />
              <Route path="plan" element={<FirstTwoWeeksPlan />} />
              <Route path="customs" element={<Customs />} />
              <Route path="untraceable" element={<Untraceable />} />
              <Route path="materials" element={<Materials />} />
              <Route path="annex" element={<AnnexI />} />
              <Route path="annex/lookup" element={<CodeLookup />} />
              <Route path="suppliers" element={<Suppliers />} />
              <Route path="suppliers/:id" element={<SupplierDetail />} />
              <Route path="risk" element={<RiskDashboard />} />
              <Route path="dds" element={<DdsRegister />} />
              <Route path="dds/dashboard" element={<DdsDashboard />} />
              <Route path="dds/:id" element={<DdsDetail />} />
              <Route path="flows" element={<Flows />} />
              <Route path="mapping" element={<Mapping />} />
              <Route path="settings/traces" element={<TracesSettings />} />
              <Route path="audit" element={<AuditLog />} />
              <Route path="vendor-plants" element={<VendorPlants />} />
              <Route path="packs" element={<Packs />} />
              <Route path="overview" element={<Overview />} />
              <Route path="account-managers" element={<Protected admin><AccountManagers /></Protected>} />
              <Route path="settings/countries" element={<CountryRisk />} />
              <Route path="settings/rules" element={<RiskRules />} />
              <Route path="settings/gfw" element={<GfwSettings />} />
              <Route path="settings/housekeeping" element={<DataHousekeeping />} />
              <Route path="settings/notifications" element={<Notifications />} />
              <Route path="code-changes/:id" element={<CodeChanges />} />
              <Route path="uploads" element={<Uploads />} />
              <Route path="scripts" element={<Scripts />} />
              <Route path="data/:type" element={<DataPage />} />
              <Route path="plants" element={<Plants />} />
              <Route
                path="users"
                element={
                  <Protected admin>
                    <UsersPage />
                  </Protected>
                }
              />
            </Route>
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </PlantProvider>
      </BrowserRouter>
      <Toaster position="top-right" richColors />
    </AuthProvider>
  );
}

export default App;
